<template>
  <b-modal
    ref="manageTariffModal"
    centered
    size="md"
    :title="tariffsModalTitle"
    hide-header-close
    scrollable
    footer-bg-variant=""
    body-class="pb-0"
    no-close-on-esc
    @hide="onHideAttempt"
    @hidden="unselectTariff(), clearErrors()">

    <b-alert
      :show="groupsEmpty"
      variant="light-danger">
      To create a tariff you need to choose a group. Currently, there are no available groups. To create a new group, click <router-link
        :to="{ name: 'SettingsGroups' }"
        class="text-info">here</router-link>.
    </b-alert>

    <b-form @submit.stop.prevent>
      <b-form-group
        id="name-tariff"
        label="Name"
        label-for="name-tariff-input">
        <b-form-input
          id="name-tariff-input"
          name="name-input"
          v-model="selectedTariff.name"
          :state="getValidationState('name')"
          aria-describedby="name-feedback"
          placeholder="Global" />
        <b-form-invalid-feedback
          v-for="(error, index) in manageTariffError('name')"
          :key="`${error.field}-${index}`">
          {{ error.message }}
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group
        id="code-tariff"
        label="Code"
        label-for="code-tariff-input">
        <b-form-input
          id="code-tariff-input"
          name="code-input"
          v-model="selectedTariff.code"
          :state="getValidationState('code')"
          aria-describedby="code-feedback"
          placeholder="global_tariff" />
        <b-form-invalid-feedback
          v-for="(error, index) in manageTariffError('code')"
          :key="`${error.field}-${index}`">
          {{ error.message }}
        </b-form-invalid-feedback>
      </b-form-group>
    </b-form>

    <b-row>
      <b-col cols="6">
        <b-form-group
          id="devices-tariff"
          label="Devices"
          label-for="devices-tariff-input">
          <b-form-input
            id="devices-tariff-input"
            type="number"
            min="0"
            step="1"
            name="devices-input"
            v-model.number="selectedTariff.devices"
            :state="getValidationState('devices')"
            aria-describedby="devices-feedback"
            placeholder="500"
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
            onwheel="this.blur()" />
          <b-form-invalid-feedback
            v-for="(error, index) in manageTariffError('devices')"
            :key="`${error.field}-${index}`">
            {{ error.message }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
      
      <b-col cols="6">
        <b-form-group
          id="users-tariff"
          label="Users"
          label-for="users-tariff-input">
          <b-form-input
            id="users-tariff-input"
            type="number"
            min="0"
            step="1"
            name="users-input"
            v-model.number="selectedTariff.users"
            :state="getValidationState('users')"
            aria-describedby="users-feedback"
            placeholder="1000"
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
            onwheel="this.blur()" />
          <b-form-invalid-feedback
            v-for="(error, index) in manageTariffError('users')"
            :key="`${error.field}-${index}`">
            {{ error.message }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col
        cols="12"
        md="6">
        <b-form-group
          id="group-tariff"
          label="Group">
          <b-form-radio-group
            v-if="!groupsEmpty"
            id="group-tariff"
            v-model="selectedTariff.group"
            name="radio-options-group"
            :state="getValidationState('group_id')"
            stacked>
            <b-form-radio
              v-for="group in groupsList"
              :key="`radio-${group._id}`"
              :value="group._id"
              size="md">
              {{ group.name }}
            </b-form-radio>
          </b-form-radio-group>
          <b-card
            v-else
            hide-header
            border-variant="light"
            align="center"
            body-class="p-0">
            <no-data-found :text="'No Groups Found'" />
          </b-card>
        </b-form-group>
      </b-col>

      <b-col
        cols="12"
        md="6">
        <b-form-group
          id="vpn-server-tariff"
          label="VPN Servers (optional)">
          <b-form-checkbox-group
            v-if="!vpnServersEmpty"
            id="groups-checkbox-group"
            v-model="selectedTariff.vpn_servers"
            name="checkbox-options"
            stacked>
            <b-form-checkbox
              v-for="vpnServer in vpnServersList"
              :key="`vpn-server-${vpnServer._id}`"
              :value="vpnServer._id"
              size="md">
              {{ vpnServer.name }}
            </b-form-checkbox>
          </b-form-checkbox-group>
          <b-card
            v-else
            hide-header
            border-variant="light"
            align="center"
            body-class="p-0">
            <no-data-found :text="'No VPN Servers Found'" />
          </b-card>
        </b-form-group>
      </b-col>
    </b-row>

    <template v-if="!selectedTariffExist">
      <div class="separator separator-solid separator-border-1 mb-7 d-none d-md-block"></div>

      <b-form-group>
        <b-form-checkbox
          id="active-checkbox"
          v-model="selectedTariff.active"
          name="active-checkbox">
          Immediately activate this tariff
        </b-form-checkbox>
      </b-form-group>
    </template>

    <template #modal-footer>
      <div class="d-flex w-100">
        <b-button
          v-b-tooltip.hover.right
          id="cancelModal"
          title="Are you sure? Unsaved data will be lost."
          variant="outline-secondary"
          size="md"
          @click="hideModal('manageTariffModal')">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          v-wave
          size="md"
          class="ml-auto"
          :disabled="tariffsResponses('manageTariffModal') || groupsEmpty"
          @click="submitTariff()">
          Submit
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  // actions
  GET_TARIFFS,
  MANAGE_TARIFF,

  // mutations
  UNSELECT_TARIFF,
  CLEAR_MANAGE_TARIFF_ERRORS
} from '@/core/services/store/tariffs.module'
import { showModal, hideModal, onHideAttempt } from '@/core/utils'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'

export default {
  name: 'ModalManageTariff',
  data() {
    return {
      showModal,
      hideModal,
      onHideAttempt,
    }
  },
  computed: {
    ...mapGetters([
      'selectedTariff',
      'selectedTariffId',
      'selectedTariffExist',
      'tariffsModalTitle',
      'tariffsResponses',
      'manageTariffErrors',
      'manageTariffError',

      'groupsList',
      'groupsEmpty',

      'vpnServersList',
      'vpnServersEmpty',
    ]),
  },
  mounted() {
    eventBus.$on('openModal', type => { this.showModal(type) })
  },
  destroyed() {
    eventBus.$off('openModal')
  },
  methods: {
    submitTariff() {
      this.$store.dispatch(MANAGE_TARIFF)
        .then(() => {
          this.hideModal('manageTariffModal')
        })
        .finally(() => {
          this.getTariffs('active')
          this.getTariffs('archieved')
        })
    },

    getTariffs(type) {
      this.$store.dispatch(GET_TARIFFS, { type })
    },

    unselectTariff() {
      this.$store.commit(UNSELECT_TARIFF)
    },

    clearErrors() {
      this.$store.commit(CLEAR_MANAGE_TARIFF_ERRORS)
    },

    getValidationState(field) {
      return this.manageTariffErrors.some(err => err.field == field) ? false : null
    },
  }
}
</script>
